import { Box } from "@mui/material";
import React from "react";
import { BasicInfo } from "@/types/dto/common/basic-info";
// eslint-disable-next-line import/no-cycle
import { PeaceOfMindIconsInfo } from "@/types/dto/landing-page/landing-page";
import { SectionHeader } from "../../common/section-header";
import PomCard from "./pom-card";

export interface PeaceOfMindProps {
  testId: string;
  sectionInfo: BasicInfo;
  icons: PeaceOfMindIconsInfo[];
  theme?: "Light" | "Dark";
  moduleRankingPosition?: string;
}

const getCardSx = (count: number) => ({
  display: "flex",
  flexDirection: "column" as const,
  flex: {
    xs: "0 0 100%",
    sm: "0 0 calc(50% - 1rem)",
    md: count === 4 ? "0 0 calc(25% - 1.5rem)" : "0 0 calc(33.333% - 1.334rem)"
  }
});

const PeaceOfMind = ({
  testId,
  sectionInfo,
  icons,
  theme = "Dark",
  moduleRankingPosition = "4"
}: PeaceOfMindProps) => {
  const cardSx = getCardSx(icons?.length ?? 0);

  return (
    <Box
      data-testid={testId}
      className="fluid-Box"
      sx={{
        backgroundColor: theme === "Dark" ? "primary.main" : "background.default",
        backgroundImage:
          theme === "Dark" ? "url(https://i.gocollette.com/img/backgrounds/pattern.svg)" : "none",
        backgroundSize: { xs: "500%", md: "350%", lg: "150%" }
      }}
      component="section"
      aria-label="peace of mind section"
      data-track="visibility"
      data-parent-container-type="peace of mind section"
      data-module-name="Peace of Mind Section"
      data-ranking-algorithm="manual"
      data-module-ranking-position={moduleRankingPosition}
    >
      <Box
        className="full-width-container"
        sx={{
          padding: {
            xs: "4rem 2.4rem 3.2rem",
            lg: "6rem 10rem"
          },
          display: "flex",
          flexDirection: "column",
          gap: "4rem"
        }}
      >
        <SectionHeader
          headerText={sectionInfo.headerText}
          headerTestId={`${testId}-header-text`}
          headerSx={{
            textTransform: "none !important",
            textAlign: "center"
          }}
          copyText={sectionInfo.copyText}
          copyTestId={`${testId}-copy-text`}
          copySx={{ fontSize: "1.8rem", textAlign: "center", "& p": { margin: 0 } }}
          sx={{
            color: theme === "Dark" ? "primary.contrastText" : "primary.main",
            maxWidth: { sm: "75rem" },
            alignSelf: "center"
          }}
          headerLevel="h2"
        />

        <Box
          data-testid={`${testId}-icons-list`}
          aria-label="peace of mind features"
          sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "2rem" }}
        >
          {icons &&
            icons.map((icon, index) => (
              <Box
                key={icon.iconAltText}
                data-testid={`${testId}-card-${icon.iconAltText}`}
                sx={cardSx}
              >
                <PomCard
                  iconUrl={icon.iconUrl}
                  iconAltText={icon.iconAltText}
                  copyText={icon.copyText}
                  headerText={icon.headerText}
                  buttonVariant={icon.buttonVariant}
                  ctaLink={icon.ctaLink}
                  ctaText={icon.ctaText}
                  index={index}
                  theme={theme}
                />
              </Box>
            ))}
        </Box>
      </Box>
    </Box>
  );
};

export default PeaceOfMind;
